/**
 * Progression popups (designs/Caged Fighter Level): level up, skill point, weekly band, streak
 * and the season's end. Shows the first queued popup, on Home and Result only (on Result after the
 * XP count-up). Also applies the in-game reduced motion setting to the menus.
 */
import { divisionName, PROGRESSION, type HistoryView, type Progress } from '@caged/shared';
import { useEffect, useId, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { uiSounds } from '../../audio/samples';
import { countryName } from '../../hud/format';
import { useLeague } from '../../state/league';
import { useApp, type Popup } from '../../state/store';
import { Rays } from '../components/Backdrop';
import { Modal } from '../components/Modal';
import { QuickRegister } from '../components/QuickRegister';
import { useDuration } from '../components/ProgressWidgets';
import ui from '../components/ui.module.css';
import { formatNumber } from '../format';
import { useReducedMotion } from '../hooks';
import { levelView, skillBarView, weeklyView } from '../progression';
import '../dev';
import '../ui.css';
import s from './PopupHost.module.css';

/** Wait before showing popups: Home settles first; Result lets the XP count-up finish. */
const DELAY_HOME = 400;
const DELAY_RESULT = 2200;

function Octagon({ tone, children }: { tone: 'amber' | 'green'; children: string }) {
  return (
    <div className={[s.octagon, tone === 'green' ? s.octGreen : ''].join(' ')} aria-hidden="true">
      <span className={s.octShadow} />
      <span className={s.octFace}>{children}</span>
    </div>
  );
}

function PopupView({ popup, progress, onClose }: { popup: Popup; progress: Progress; onClose: () => void }) {
  const { t } = useTranslation();
  const go = useApp((st) => st.go);
  const duration = useDuration();
  const titleId = useId();
  const now = Date.now();

  switch (popup.kind) {
    case 'levelUp': {
      const lv = levelView(progress.xpTotal);
      return (
        <Modal onClose={onClose} labelledBy={titleId} variant="center" className={s.card} scene={<Rays size={640} color="rgba(245,160,60,.2)" top="46%" />}>
          <div className={s.kicker}>{t('screens.popups.levelUp.kicker')}</div>
          <Octagon tone="amber">{String(popup.level)}</Octagon>
          <h2 id={titleId} className={s.title}>
            {t('screens.level.level', { level: popup.level })}
          </h2>
          <p className={s.body}>
            {t('screens.popups.levelUp.body')}
            <br />
            {lv.maxed ? t('screens.level.max') : t('screens.popups.levelUp.next', { xp: formatNumber(lv.left) })}
          </p>
          <button type="button" className={[ui.primary, s.btn].join(' ')} onClick={onClose}>
            {t('screens.popups.levelUp.cta')}
            <span aria-hidden="true">▷</span>
          </button>
        </Modal>
      );
    }
    case 'skillPoint': {
      const sp = skillBarView(progress.skillPointsEarned, progress.skillBarXp);
      return (
        <Modal
          onClose={onClose}
          labelledBy={titleId}
          variant="center"
          className={[s.card, s.cardGreen].join(' ')}
          scene={<Rays size={640} color="rgba(127,214,106,.16)" top="46%" />}
        >
          <div className={s.kicker}>{t('screens.popups.skillPoint.kicker')}</div>
          <Octagon tone="green">{`+${popup.gained}`}</Octagon>
          <div className={s.titleBlock}>
            <h2 id={titleId} className={s.title}>
              {t('screens.popups.skillPoint.title', { count: popup.gained })}
            </h2>
            <span className={s.greenChip}>{t('screens.popups.skillPoint.toSpend', { count: popup.available })}</span>
          </div>
          <p className={s.body}>{t('screens.popups.skillPoint.next', { xp: formatNumber(sp.left) })}</p>
          <div className={s.btnPair}>
            <button
              type="button"
              className={[ui.primary, s.btn, s.btnMain].join(' ')}
              onClick={() => {
                onClose();
                go('training');
              }}
            >
              {t('screens.popups.skillPoint.train')}
              <span aria-hidden="true">→</span>
            </button>
            <button type="button" className={[ui.textBtn, s.later].join(' ')} onClick={onClose}>
              {t('screens.popups.later')}
            </button>
          </div>
        </Modal>
      );
    }
    case 'keep': {
      // A guest's level or skill points, and registering right here to keep them (D41).
      const leveled = popup.level !== null;
      return (
        <Modal
          onClose={onClose}
          labelledBy={titleId}
          variant="center"
          className={[s.card, s.cardGreen, s.keepCard].join(' ')}
          scene={<Rays size={640} color={leveled ? 'rgba(245,160,60,.2)' : 'rgba(127,214,106,.16)'} top="46%" />}
        >
          <div className={s.keepGain}>
            <div className={s.kicker}>{t(leveled ? 'screens.popups.levelUp.kicker' : 'screens.popups.skillPoint.kicker')}</div>
            <Octagon tone={leveled ? 'amber' : 'green'}>{leveled ? String(popup.level) : `+${popup.points}`}</Octagon>
            <div className={s.keepChips}>
              {leveled && <span className={s.amberChip}>{t('screens.level.level', { level: popup.level })}</span>}
              {popup.points > 0 && <span className={s.greenChip}>{t('screens.popups.skillPoint.title', { count: popup.points })}</span>}
            </div>
          </div>
          <div className={s.keepMain}>
            <div className={s.keepText}>
              <h2 id={titleId} className={s.title}>
                {t('screens.auth.registerTitle')}
              </h2>
              <p className={[s.body, s.keepSub].join(' ')}>{t('screens.auth.registerSub')}</p>
            </div>
            <QuickRegister onLeave={onClose} />
            <button type="button" className={[ui.textBtn, s.later, s.keepLater].join(' ')} onClick={onClose}>
              {t('screens.popups.later')}
            </button>
          </div>
        </Modal>
      );
    }
    case 'weeklyBand': {
      const w = weeklyView({ ...progress, weekFights: popup.fights }, now);
      const pct = Math.round(popup.factor * 100);
      const zero = w.bands[w.bands.length - 1];
      return (
        <Modal onClose={onClose} labelledBy={titleId} variant="sheet" className={s.sheet}>
          <div className={s.bandHead}>
            <div className={s.bandIcon} aria-hidden="true">
              {popup.fights}
            </div>
            <div className={s.bandHeadText}>
              <span className={ui.label}>{t('screens.popups.weekly.kicker')}</span>
              <h2 id={titleId} className={s.bandTitle}>
                {t('screens.popups.weekly.title', { count: popup.fights })}
              </h2>
            </div>
          </div>
          <p className={s.bandBody}>
            <Trans
              i18nKey={popup.factor === 0 ? 'screens.popups.weekly.bodyZero' : 'screens.popups.weekly.body'}
              values={{ count: popup.fights, pct }}
              components={{ b: <b className={s.amber} /> }}
            />
          </p>
          <div className={s.bandMeter} aria-hidden="true">
            <div className={s.bandBars}>
              {w.bands.map((b) => {
                const passed = b.pct > pct;
                return (
                  <div
                    key={b.range}
                    className={s.bandSeg}
                    style={{ flex: b.flex, background: b.current ? 'rgba(245,160,60,.35)' : passed ? 'var(--amber)' : 'rgba(255,255,255,.06)' }}
                  />
                );
              })}
            </div>
            <div className={s.bandLabels}>
              {w.bands.map((b) => (
                <span key={b.range} className={b.current ? s.amber : ''} style={{ flex: b.flex }}>
                  {b.range}
                  <b>{b.pct}%</b>
                </span>
              ))}
            </div>
          </div>
          <div className={s.bandNotes}>
            {zero && <span>{t('screens.popups.weekly.coinsOnly', { range: zero.range })}</span>}
            <span>{t('screens.popups.weekly.resets', { time: duration(w.resetAt - now) })}</span>
          </div>
          <button type="button" className={[ui.secondary, s.gotIt].join(' ')} onClick={onClose}>
            {t('screens.popups.gotIt')}
          </button>
        </Modal>
      );
    }
    case 'season':
      return <SeasonPopup history={popup.season} onClose={onClose} titleId={titleId} />;
    case 'streak': {
      const days = Array.from({ length: PROGRESSION.xp.streakDays }, (_, i) => i + 1);
      return (
        <Modal onClose={onClose} labelledBy={titleId} variant="center" className={s.card} scene={<Rays size={640} color="rgba(245,160,60,.2)" top="46%" />}>
          <div className={s.kicker}>{t('screens.popups.streak.kicker')}</div>
          <div className={s.days} aria-hidden="true">
            {days.map((d) => (
              <div key={d} className={s.day}>
                <span className={s.dayLabel}>{t('screens.popups.streak.day')}</span>
                <span className={s.dayNum}>{d}</span>
              </div>
            ))}
          </div>
          <div className={s.titleBlock}>
            <h2 id={titleId} className={s.title}>
              {t('screens.popups.streak.title', { days: PROGRESSION.xp.streakDays })}
            </h2>
            <div className={s.bigXp}>{t('screens.popups.streak.xp', { xp: popup.xp })}</div>
          </div>
          <p className={s.body}>{t('screens.popups.streak.body')}</p>
          <button type="button" className={[ui.primary, s.btn].join(' ')} onClick={onClose}>
            {t('screens.popups.streak.cta')}
            <span aria-hidden="true">→</span>
          </button>
        </Modal>
      );
    }
  }
}

/** A league season's end (docs/game-design.md §7.9): champion, promotion, relegation or the table place. */
function SeasonPopup({ history: h, onClose, titleId }: { history: HistoryView; onClose: () => void; titleId: string }) {
  const { t, i18n } = useTranslation();
  const openLeague = useApp((st) => st.openLeague);
  const close = () => {
    useLeague.getState().seen([h.season]);
    onClose();
  };
  const division = (d: number) => t(`screens.league.divisions.${divisionName(d)}`);
  const up = h.outcome === 'champion' || h.outcome === 'promoted' || !!h.belt;
  // Moving to another country's league (L8): promoted or relegated doesn't apply to them.
  const title = h.movedTo
    ? h.outcome === 'champion'
      ? t('screens.popups.season.champion')
      : h.belt
        ? t('screens.popups.season.won')
        : t('screens.popups.season.over')
    : h.outcome === 'champion'
      ? t('screens.popups.season.champion')
      : h.outcome === 'promoted'
        ? h.belt
          ? t('screens.popups.season.wonPromoted')
          : t('screens.popups.season.promoted')
        : h.outcome === 'relegated'
          ? t('screens.popups.season.relegated')
          : h.belt
            ? t('screens.popups.season.won')
            : t('screens.popups.season.over');
  const body = h.movedTo
    ? t('screens.popups.season.movedBody', { pos: h.position, country: countryName(h.movedTo, i18n.language) })
    : h.outcome === 'champion'
      ? t('screens.popups.season.championBody')
      : h.outcome === 'promoted'
        ? t(h.freePromotion && h.qualifier === 'lost' ? 'screens.popups.season.luckyBody' : h.qualifier === 'won' ? 'screens.popups.season.qualifierBody' : 'screens.popups.season.promotedBody', {
            division: division(h.division - 1),
          })
        : h.outcome === 'relegated'
          ? t('screens.popups.season.relegatedBody', { division: division(h.division + 1) })
          : t('screens.popups.season.stayedBody', { pos: h.position });
  return (
    <Modal
      onClose={close}
      labelledBy={titleId}
      variant="center"
      className={[s.card, up ? '' : s.cardDim].join(' ')}
      scene={up ? <Rays size={640} color="rgba(245,160,60,.2)" top="46%" /> : undefined}
    >
      <div className={s.kicker}>
        {t('screens.popups.season.kicker', { season: h.season, series: h.division === 1 ? division(1) : `${division(h.division)} ${h.series}` })}
      </div>
      <Octagon tone={h.outcome === 'relegated' ? 'amber' : 'green'}>{h.belt ? '🏆' : `#${h.position}`}</Octagon>
      <div className={s.titleBlock}>
        <h2 id={titleId} className={s.title}>
          {title}
        </h2>
        {h.bonusPoints > 0 && <div className={s.bigXp}>{t('screens.popups.season.points', { count: h.bonusPoints })}</div>}
      </div>
      <p className={s.body}>
        {body}
        {h.belt && (
          <>
            <br />
            {t(h.belt === 'national' ? 'screens.popups.season.nationalBelt' : 'screens.popups.season.seriesBelt')}
          </>
        )}
      </p>
      <button
        type="button"
        className={[ui.primary, s.btn].join(' ')}
        onClick={() => {
          close();
          openLeague('league');
        }}
      >
        {t('screens.popups.season.cta')}
        <span aria-hidden="true">→</span>
      </button>
    </Modal>
  );
}

export function PopupHost() {
  const popups = useApp((st) => st.popups);
  const screen = useApp((st) => st.screen);
  const progress = useApp((st) => st.progress);
  const dismissPopup = useApp((st) => st.dismissPopup);
  const reducedSetting = useApp((st) => st.settings.reducedMotion);
  const reduced = useReducedMotion();
  const [readyFor, setReadyFor] = useState<string | null>(null);

  // Settings → Reduced motion also calms the menus (ui.css).
  useEffect(() => {
    document.documentElement.classList.toggle('cg-reduce-motion', reducedSetting);
  }, [reducedSetting]);

  useEffect(() => {
    setReadyFor(null);
    if (screen !== 'home' && screen !== 'result') return;
    const delay = screen === 'result' ? (reduced ? 300 : DELAY_RESULT) : DELAY_HOME;
    const id = setTimeout(() => setReadyFor(screen), delay);
    return () => clearTimeout(id);
  }, [screen, reduced]);

  // A fight to rejoin comes first (its card is on Home).
  const rejoin = useApp((st) => st.rejoin);
  const popup = rejoin ? undefined : popups[0];
  const shown = popup && readyFor === screen ? popup : null;
  // A jingle when a progression popup opens.
  useEffect(() => {
    if (shown) uiSounds().popup();
  }, [shown]);
  if (!popup || readyFor !== screen) return null;
  return <PopupView key={`${popup.kind}-${popups.length}`} popup={popup} progress={progress} onClose={dismissPopup} />;
}
